import { useState, useEffect, useRef } from "react";

export type AudioOnlyReason = "degraded_connection" | "avatar_unavailable" | null;

export type BannerState =
  | "mid_call_instability"    // quality dropped mid-call — brief warning before switch
  | "pre_call_degraded" // now in audio-only mode
  | null;

interface UseAudioOnlyModeProps {
  /** True if this is the user's very first simulation — must never start audio-only */
  firstScenario: boolean | null;
  /** Whether LiveKit is fully connected */
  isConnected: boolean;
  /** LiveKit ConnectionQuality string value, e.g. 'poor', 'lost', 'good' */
  connectionQuality: string;
  /** Whether a remote audio track from the avatar has been received */
  hasAudioTrack: boolean;
  /** Whether a remote video track from the avatar has been received */
  hasVideoTrack: boolean;
  /** True when the avatar video element has stopped producing frames (freeze detected) */
  isVideoFrozen?: boolean;
  /** Server-side override: pre-call check determined audio-only is needed */
  forceAudioOnly?: boolean;
}

export interface AudioOnlyModeResult {
  isAudioOnly: boolean;
  audioOnlyReason: AudioOnlyReason;
  bannerState: BannerState;
  /** Try to exit audio-only mode. User-initiated — does not respect the auto-lock. */
  tryVideo: () => void;
}

const POOR_QUALITIES = new Set(["poor", "lost"]);

export function useAudioOnlyMode({
  firstScenario,
  isConnected,
  connectionQuality,
  hasAudioTrack,
  isVideoFrozen = false,
  forceAudioOnly = false,
}: UseAudioOnlyModeProps): AudioOnlyModeResult {
  const isFirstEver = firstScenario === true;
  const isPoor = POOR_QUALITIES.has(connectionQuality?.toLowerCase?.() ?? "");

  const [isAudioOnly, setIsAudioOnly] = useState(forceAudioOnly);
  const [audioOnlyReason, setAudioOnlyReason] = useState<AudioOnlyReason>(
    forceAudioOnly ? "avatar_unavailable" : null
  );
  const [bannerState, setBannerState] = useState<BannerState>(null);

  const audioOnlyLockedRef = useRef(forceAudioOnly);
  const sessionStartedRef = useRef(false);

  useEffect(() => {
    console.log("[useAudioOnlyMode] bannerState →", bannerState);
  }, [bannerState]);

  // ── Rule 1: forceAudioOnly ────────────────────────────────────────────────
  // Pre-call check determined audio-only is needed. Lock in immediately and
  // show the orange banner once the BoxLoader closes (hasAudioTrack arriving).
  useEffect(() => {
    if (!forceAudioOnly) return;
    audioOnlyLockedRef.current = true;
    setIsAudioOnly(true);
    setAudioOnlyReason("avatar_unavailable");
    if (hasAudioTrack) {
      setBannerState("pre_call_degraded");
    }
  }, [forceAudioOnly, hasAudioTrack]);

  // ── Track session start ───────────────────────────────────────────────────
  useEffect(() => {
    if (isConnected && !sessionStartedRef.current) {
      sessionStartedRef.current = true;
    }
  }, [isConnected]);

  // ── Rule 2: mid-call quality degrades or video freezes ────────────────────
  // Show red banner immediately, then switch to audio-only after a short delay
  // so the user sees the warning before the mode changes.
  useEffect(() => {
    if (!sessionStartedRef.current || !isConnected) return;
    if (audioOnlyLockedRef.current) return;
    if (isFirstEver) return;

    if (isPoor || isVideoFrozen) {
      setBannerState("mid_call_instability");
      const timer = setTimeout(() => {
        audioOnlyLockedRef.current = true;
        setIsAudioOnly(true);
        setAudioOnlyReason("degraded_connection");
      }, 1_500);
      return () => clearTimeout(timer);
    } else if (bannerState === "mid_call_instability") {
      setBannerState(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPoor, isVideoFrozen, isConnected]);

  // ── Try Video: user-initiated — breaks the auto-lock ─────────────────────
  // No-op when forceAudioOnly: there is no avatar video to switch back to.
  const tryVideo = () => {
    if (forceAudioOnly) return;
    audioOnlyLockedRef.current = false;
    sessionStartedRef.current = false;
    setIsAudioOnly(false);
    setAudioOnlyReason(null);
    setBannerState(null);
  };

  return { isAudioOnly, audioOnlyReason, bannerState, tryVideo };
}
